import Button from 'tailwind-components/Button/Button';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Tabs from 'components/Tabs/Tabs';
import Tab from '@mui/material/Tab';
import ScrollableTabs from 'tailwind-components/ScrollableTabs/ScrollableTabs';
import useTabUrlState from 'hooks/useTabUrlState';
import { getCompany } from 'helpers/getCompanyVariables';
import styles from '../AgencySettings.module.scss';
import { Mail } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { showAlert } from 'state/actions/actions';
import { SendAgencyFormEmail } from 'state/actions/agencyManagement';

import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField
} from '@mui/material';
import Separator from 'tailwind-components/Separator/Separator';

const EMAIL_TAB = {
  MYSELF: 'myself',
  AGENT: 'agent'
};

const SignupAgents = ({ zohoData, Healthsherpa_Agency_Code }) => {
  const company = getCompany();
  const isOneill = company === 'oneill';

  const validateEmail = inputEmail => {
    const emailEmpty = emails.length === 0;
    const emailRegex = /^[a-zA-Z0-9._+%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return !emailEmpty && emailRegex.test(inputEmail);
  };
  const agencyManagement = useSelector(state => state.agencyManagement);

  const [typeOfForm, setTypeOfForm] = useState('downlineAgent');
  const [typeOfFormToAgents, setTypeOfFormToAgent] = useState('downlineAgent');
  const [activeEmailTab, setActiveEmailTab] = useTabUrlState({
    param: 'emailTab',
    tabIds: Object.values(EMAIL_TAB),
    defaultTabId: EMAIL_TAB.AGENT,
    correctInvalidToDefault: true
  });

  const [emails, setEmails] = useState('');
  const [onboardingFirstName, setOnboardingFirstName] = useState('');
  const [onboardingLastName, setOnboardingLastName] = useState('');
  const [onboardingNPN, setOnboardingNPN] = useState('');

  const handleOnboardingEmailChange = data => {
    if (validateEmail(data.target.value)) setOnboardingEmailFieldError(false);
    setEmails(data.target.value);
  };
  const handleOnboardingFirstNameChange = data => {
    setOnboardingFirstNameFieldError(false);
    setOnboardingFirstName(data.target.value);
  };
  const handleOnboardingLastNameChange = data => {
    setOnboardingLastNameFieldError(false);
    setOnboardingLastName(data.target.value);
  };
  const handleOboardingNPNChange = data => {
    setOnboardingNpnFieldError(false);
    setOnboardingNPN(data.target.value);
  };
  const emptyAllBoxes = () => {
    setEmails('');
    setOnboardingFirstName('');
    setOnboardingLastName('');
    setOnboardingNPN('');
  };

  const dispatch = useDispatch();

  const handleSelectFormType = ({ target }) => {
    setTypeOfForm(target.defaultValue);
  };

  const handleSelectFormTypeToAgents = ({ target }) => {
    setTypeOfFormToAgent(target.defaultValue);
  };

  const handleSendOnboardingForm = async (type, emailToType) => {
    if (emailToType === '1') {
      await dispatch(SendAgencyFormEmail([zohoData.Email], type));
    } else {
      if (firstNameEmpty) setOnboardingFirstNameFieldError(true);
      if (lastNameEmpty) setOnboardingLastNameFieldError(true);
      if (npnEmpty) setOnboardingNpnFieldError(true);
      if (!validEmail) setOnboardingEmailFieldError(true);
      if (firstNameEmpty || lastNameEmpty || npnEmpty || !validEmail) return;
      const emailExist = agencyManagement.agencyDashboardAgents
        .map(x => x.Email)
        .includes(emails);
      const npnExist = agencyManagement.agencyDashboardAgents
        .map(x => x.NPN)
        .includes(onboardingNPN);
      if (emailExist || npnExist) {
        const msg = `This ${emailExist ? 'Email' : ''}${
          emailExist && npnExist ? ' and ' : ''
        }${npnExist ? 'NPN' : ''} already exists in this agency.`;
        dispatch(showAlert({ message: msg, code: 'error' }));
        return;
      }
      await dispatch(
        SendAgencyFormEmail(
          [emails],
          type,
          onboardingFirstName,
          onboardingLastName,
          onboardingNPN
        )
      );
      emptyAllBoxes();
    }
  };

  const {
    validEmail,
    firstNameEmpty,
    lastNameEmpty,
    npnEmpty,
    onboardingInvitesDisabled
  } = React.useMemo(() => {
    const validEmail = validateEmail(emails);
    const firstNameEmpty = onboardingFirstName.length === 0;
    const lastNameEmpty = onboardingLastName.length === 0;
    const npnEmpty = onboardingNPN.length === 0;
    const isSendingForm = agencyManagement.isSendingOnboardingForm
      ? true
      : false;
    const onboardingInvitesDisabled = isSendingForm;
    return {
      validEmail,
      firstNameEmpty,
      lastNameEmpty,
      npnEmpty,
      onboardingInvitesDisabled
    };
  }, [
    emails,
    onboardingFirstName,
    onboardingLastName,
    onboardingNPN,
    agencyManagement.isSendingOnboardingForm
  ]);

  const [onboardingFirstNameFieldError, setOnboardingFirstNameFieldError] =
    useState(false);
  const [onboardingLastNameFieldError, setOnboardingLastNameFieldError] =
    useState(false);
  const [onboardingNpnFieldError, setOnboardingNpnFieldError] = useState(false);
  const [onboardingEmailFieldError, setOnboardingEmailFieldError] =
    useState(false);

  const emailMyselfRounded = isOneill ? 'small' : 'large';
  const emailAgentFill = !isOneill;

  const emailMyselfPanel = (
    <div className={styles.emailTo}>
      {isOneill ? null : <br />}
      {isOneill ? (
        <>
          <p className='font-semibold'>Select Type of Onboarding Form:</p>
          <RadioGroup
            row
            name='email-to-myself'
            defaultValue={typeOfForm}
            onChange={handleSelectFormType}
          >
            <FormControlLabel
              value='downlineAgent'
              control={<Radio />}
              label='Downline Agent (Default)'
            />
            <FormControlLabel
              value='downlineAgencyAdmin'
              control={<Radio />}
              label='Downline Agency Admin'
            />
          </RadioGroup>
        </>
      ) : null}
      {typeOfForm === 'downlineAgencyAdmin' && (
        <p className={styles.warning}>
          Warning: Any individual that is provided this link will have admin
          privileges and access to the downline agency that they create.
        </p>
      )}
      {isOneill && !Healthsherpa_Agency_Code && (
        <p className={styles.warning}>
          Warning: You have not provided your Health Sherpa code in the field
          above, so any emails sent directly to agents will only provide them
          with the O&apos;Neill Marketing HS code.
        </p>
      )}
      <Button
        disabled={agencyManagement.isSendingOnboardingForm}
        isLoading={agencyManagement.isSendingOnboardingForm}
        variant='primaryDarkened'
        rounded={emailMyselfRounded}
        size='medium'
        text='Email me a secure link'
        icon={<Mail size={20} />}
        onClick={() => handleSendOnboardingForm(typeOfForm, '1')}
      />
    </div>
  );

  const emailAgentPanel = (
    <>
      <div className={styles.emailTo}>
        <p>
          Send an email with the{' '}
          <span className='font-medium'>Agent Onboarding Form</span>:
        </p>
        <div className={styles.onBoardingInputsContaier}>
          <TextField
            id='newOboarding_FirstName'
            label='First Name'
            variant='outlined'
            value={onboardingFirstName}
            onChange={handleOnboardingFirstNameChange}
            required
            color={onboardingFirstNameFieldError ? 'error' : null}
            error={onboardingFirstNameFieldError}
          />

          <TextField
            id='newOboarding_LastName'
            label='Last Name'
            variant='outlined'
            value={onboardingLastName}
            onChange={handleOnboardingLastNameChange}
            required
            color={onboardingLastNameFieldError ? 'error' : null}
            error={onboardingLastNameFieldError}
          />

          <TextField
            id='newOboarding_NPN'
            label='NPN'
            variant='outlined'
            value={onboardingNPN}
            onChange={handleOboardingNPNChange}
            required
            color={onboardingNpnFieldError ? 'error' : null}
            error={onboardingNpnFieldError}
          />

          <TextField
            id='newOboarding_Email'
            label='Email'
            variant='outlined'
            value={emails}
            onChange={handleOnboardingEmailChange}
            required
            color={onboardingEmailFieldError ? 'error' : null}
            error={onboardingEmailFieldError}
          />
        </div>
        {isOneill && !Healthsherpa_Agency_Code && (
          <p className={styles.warning}>
            Warning: You have not provided your Health Sherpa code in the field
            above, so any emails sent directly to agents will only provide them
            with the O&apos;Neill Marketing HS code.
          </p>
        )}
        {isOneill ? (
          <>
            <p className='font-semibold'>Select Type of Onboarding Form:</p>
            <RadioGroup
              row
              name='email-to-agent'
              defaultValue={typeOfFormToAgents}
              onChange={handleSelectFormTypeToAgents}
            >
              <FormControlLabel
                value='downlineAgent'
                control={<Radio />}
                label='Downline Agent (Default)'
              />
              <FormControlLabel
                value='downlineAgencyAdmin'
                control={<Radio />}
                label='Downline Agency Admin'
              />
            </RadioGroup>
          </>
        ) : null}
        {typeOfFormToAgents === 'downlineAgencyAdmin' && (
          <p className={styles.warning}>
            Warning: Any individual that is provided this link will have admin
            privileges and access to the downline agency that they create.
          </p>
        )}
      </div>
      <Button
        fill={emailAgentFill}
        disabled={onboardingInvitesDisabled}
        isLoading={agencyManagement.isSendingOnboardingForm}
        variant='primaryDarkened'
        rounded='small'
        size='medium'
        text='Email Onboarding form to Agent'
        icon={<Mail size={20} />}
        onClick={() => handleSendOnboardingForm(typeOfFormToAgents, '2')}
      />
    </>
  );

  if (isOneill) {
    return (
      <div className='flex w-full min-w-0 flex-col'>
        <div></div>
        <ScrollableTabs
          value={activeEmailTab}
          onChange={(_event, nextTab) => setActiveEmailTab(nextTab)}
          tabPaddingY={1}
          aria-label='Onboarding email options'
        >
          <Tab value={EMAIL_TAB.AGENT} label='Email Agent' />
          <Tab value={EMAIL_TAB.MYSELF} label='Email Myself' />
        </ScrollableTabs>
        <div className='shrink-0'>
          <Separator />
        </div>
        {activeEmailTab === EMAIL_TAB.MYSELF && emailMyselfPanel}
        {activeEmailTab === EMAIL_TAB.AGENT && emailAgentPanel}
      </div>
    );
  }

  return (
    <>
      <h4 className='mt-8!'>Step 2: Send Agent Onboarding Emails</h4>
      <FormControl sx={{ width: '100%' }}>
        <Tabs
          headers={['Email Agent', 'Email Myself']}
          initialTab={0}
          bottomLine={true}
        >
          {emailAgentPanel}
          {emailMyselfPanel}
        </Tabs>
      </FormControl>
    </>
  );
};

SignupAgents.propTypes = {
  zohoData: PropTypes.object,
  Healthsherpa_Agency_Code: PropTypes.string
};

export default React.memo(SignupAgents, (prevProps, nextProps) => {
  return (
    prevProps.Healthsherpa_Agency_Code === nextProps.Healthsherpa_Agency_Code
  );
});
